// @ts-nocheck
/* ============================================================
   Slabs drawn on the satellite view (plan §5.1, roadmap §13 item 9).

   The fence estimator traces a line and prices feet; a concrete job is AREAS —
   the driveway section by the garage, the front walk, the patio. This is the
   pure geometry behind "Mark the slabs" (concretemap.tsx): square feet from a
   lat/lng ring, snapping a corner to the house footprint, another slab or a
   right angle, a rectangle from two corners aligned to the house, and how the
   drawn slabs become the estimator's blocks (area, sections, surfaces per job
   type). Nothing here touches the DOM or Leaflet; unit-slabs.mjs runs it as-is.

   Stored on the quote as inputs.concrete.slabs = [{ id, kind, jobType, poly:
   [lat,lng][], sqft, sections, label }] and inputs.concrete.site = { center,
   address, house, footprints, parcel, source } — under `concrete`, never as
   inputs.parcel, so nothing fence-shaped switches on (plan §5.1).
   ============================================================ */

export const M_TO_FT = 3.28084;
export const FT_TO_M = 1 / M_TO_FT;

/* the kinds of concrete a slab can be (the estimator's SURFACES ids) and the
   job type each is drawn for by default: a garage floor gets a coating, the
   rest get lifted. unit-slabs.mjs pins the labels equal to engine.concrete.ts. */
export const SLAB_KINDS: { id: string; label: string; job: string }[] = [
  { id: "driveway", label: "Driveway", job: "lift" }, { id: "apron", label: "Garage apron", job: "lift" }, { id: "walk", label: "Sidewalk / walkway", job: "lift" },
  { id: "patio", label: "Patio", job: "lift" }, { id: "pool-deck", label: "Pool deck", job: "lift" }, { id: "steps", label: "Steps / stoop", job: "lift" },
  { id: "porch", label: "Porch", job: "lift" }, { id: "garage-floor", label: "Garage / shop floor", job: "coat" }, { id: "basement", label: "Basement floor", job: "coat" },
  { id: "ac-pad", label: "AC pad", job: "lift" }, { id: "other", label: "Other slab", job: "lift" },
];
export const kindLabel = (id: any): string => { const k = SLAB_KINDS.find((x) => x.id === id); return k ? k.label : "Slab"; };
export const jobForKind = (id: any): string => { const k = SLAB_KINDS.find((x) => x.id === id); return k ? k.job : "lift"; };

/* ---- local planar metres around an origin (the same projection the estimator and buildings.ts use) ---- */
export const toXY = (p: any, o: any): [number, number] => { const k = Math.cos(o[0] * Math.PI / 180); return [(p[1] - o[1]) * k * 111320, (p[0] - o[0]) * 110540]; };
export const toLL = (xy: any, o: any): [number, number] => { const k = Math.cos(o[0] * Math.PI / 180); return [o[0] + xy[1] / 110540, o[1] + xy[0] / (k * 111320)]; };
const ring = (poly: any): [number, number][] => (Array.isArray(poly) ? poly.filter((p) => Array.isArray(p) && isFinite(Number(p[0])) && isFinite(Number(p[1]))).map((p) => [Number(p[0]), Number(p[1])] as [number, number]) : []);

export function centroid(poly: any): [number, number] | null {
  const r = ring(poly); if (!r.length) return null;
  let la = 0, ln = 0; r.forEach((p) => { la += p[0]; ln += p[1]; });
  return [la / r.length, ln / r.length];
}
export function edgeFt(a: any, b: any): number {
  if (!a || !b) return 0;
  const xy = toXY(b, a);
  return Math.hypot(xy[0], xy[1]) * M_TO_FT;
}
/* square feet of a lat/lng ring (shoelace on local metres) */
export function areaFt2(poly: any): number {
  const r = ring(poly); if (r.length < 3) return 0;
  const o = centroid(r)!; let a = 0;
  for (let i = 0; i < r.length; i++) { const A = toXY(r[i], o), B = toXY(r[(i + 1) % r.length], o); a += A[0] * B[1] - B[0] * A[1]; }
  return Math.abs(a / 2) * M_TO_FT * M_TO_FT;
}
export function perimeterFt(poly: any): number {
  const r = ring(poly); if (r.length < 2) return 0;
  let s = 0; for (let i = 0; i < r.length; i++) s += edgeFt(r[i], r[(i + 1) % r.length]);
  return s;
}
/* the longest edge's length and bearing (degrees from east, counter-clockwise) */
export function longestEdge(poly: any): { ft: number; deg: number } | null {
  const r = ring(poly); if (r.length < 2) return null;
  let best: any = null;
  for (let i = 0; i < r.length; i++) {
    const a = r[i], b = r[(i + 1) % r.length]; const xy = toXY(b, a); const ft = Math.hypot(xy[0], xy[1]) * M_TO_FT;
    if (!best || ft > best.ft) best = { ft, deg: Math.atan2(xy[1], xy[0]) * 180 / Math.PI };
  }
  return best;
}

/* the engine's default: a section is about 100 sq ft (engine.concrete.ts sectionsOf) */
export const sectionsFor = (sqft: any): number => Math.max(1, Math.ceil((Number(sqft) || 0) / 100));

let seq = 0;
export function newSlab(poly: any, kind: any, jobType?: any, id?: any) {
  const r = ring(poly);
  const sqft = Math.round(areaFt2(r));
  const k = SLAB_KINDS.some((x) => x.id === kind) ? kind : "other";
  return { id: id || ("slab-" + Date.now().toString(36) + "-" + (++seq)), kind: k, jobType: jobType || jobForKind(k), poly: r, sqft, sections: sectionsFor(sqft) };
}
export const withPoly = (slab: any, poly: any) => { const r = ring(poly); const sqft = Math.round(areaFt2(r)); return { ...slab, poly: r, sqft, sections: sectionsFor(sqft) }; };
export function slabLabel(slab: any): string {
  if (!slab) return "";
  const parts = [kindLabel(slab.kind), `${Math.round(Number(slab.sqft) || 0).toLocaleString("en-US")} sq ft`];
  if (slab.jobType === "lift" && Number(slab.sections) > 0) parts.push(`${slab.sections} section${slab.sections === 1 ? "" : "s"}`);
  return parts.join(" · ");
}

/* ---- snapping ----
   A corner lands on: the nearest corner of the house / another slab within
   `tolFt`; else the nearest point on one of their edges within `tolFt`; else,
   while tracing, square to the previous edge when it is within 7° of a right
   angle (or to the house's grid when it's the second point); else where the
   finger went. Returns the point and what it snapped to. */
const nearestOnSeg = (p: any, a: any, b: any) => {
  const dx = b[0] - a[0], dy = b[1] - a[1]; const L2 = dx * dx + dy * dy;
  const t = L2 ? Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / L2)) : 0;
  return [a[0] + dx * t, a[1] + dy * t];
};
export function snapPoint(pt: any, ctx: any = {}, tolFt = 3) {
  if (!pt) return { pt, kind: null };
  const o = pt;
  const P = toXY(pt, o);
  const tol = tolFt * FT_TO_M;
  const rings: [number, number][][] = [];
  (Array.isArray(ctx.footprints) ? ctx.footprints : []).forEach((f) => { const r = ring(f); if (r.length >= 2) rings.push(r); });
  (Array.isArray(ctx.slabs) ? ctx.slabs : []).forEach((s) => { const r = ring(s && s.poly ? s.poly : s); if (r.length >= 2) rings.push(r); });
  if (Array.isArray(ctx.draft) && ctx.draft.length >= 2) rings.push(ring(ctx.draft).slice(0, -1));   // earlier points of the slab being traced, not the one before the cursor
  // 1) a corner
  let best: any = null;
  rings.forEach((r) => r.forEach((v) => { const d = Math.hypot(...((xy) => [xy[0] - P[0], xy[1] - P[1]])(toXY(v, o))); if (d <= tol && (!best || d < best.d)) best = { d, pt: v, kind: "vertex" }; }));
  if (best) return { pt: [best.pt[0], best.pt[1]], kind: "vertex" };
  // 2) an edge
  rings.forEach((r) => { const closed = r.length >= 3; for (let i = 0; i < (closed ? r.length : r.length - 1); i++) {
    const A = toXY(r[i], o), B = toXY(r[(i + 1) % r.length], o); const Q = nearestOnSeg(P, A, B); const d = Math.hypot(Q[0] - P[0], Q[1] - P[1]);
    if (d <= tol && (!best || d < best.d)) best = { d, xy: Q, kind: "edge" };
  } });
  if (best) return { pt: toLL(best.xy, o), kind: "edge" };
  // 3) square to the previous edge (or to the house's grid on the second point)
  const prev = ctx.prev ? toXY(ctx.prev, o) : null;
  if (prev) {
    const v = [P[0] - prev[0], P[1] - prev[1]]; const len = Math.hypot(v[0], v[1]);
    if (len > tol) {
      let axisDeg: number | null = null;
      if (ctx.prevPrev) { const u = [prev[0] - toXY(ctx.prevPrev, o)[0], prev[1] - toXY(ctx.prevPrev, o)[1]]; axisDeg = Math.atan2(u[1], u[0]) * 180 / Math.PI; }
      else if (typeof ctx.axisDeg === "number") axisDeg = ctx.axisDeg;
      if (axisDeg != null) {
        const ang = Math.atan2(v[1], v[0]) * 180 / Math.PI;
        const rel = ((ang - axisDeg) % 90 + 90) % 90;   // how far off the nearest multiple of 90° from the axis
        const off = Math.min(rel, 90 - rel);
        if (off <= 7) {
          const target = ang - (rel <= 45 ? rel : rel - 90);
          const t = target * Math.PI / 180;
          return { pt: toLL([prev[0] + Math.cos(t) * len, prev[1] + Math.sin(t) * len], o), kind: "ortho" };
        }
      }
    }
  }
  return { pt: [pt[0], pt[1]], kind: null };
}

/* is the cursor back on the first corner (closing the trace)? */
export const closesAt = (pt: any, first: any, tolFt = 4) => !!(pt && first && edgeFt(pt, first) <= tolFt);

/* the bearing (degrees from east) of the nearest footprint edge within maxFt
   of a point — the grid a driveway or apron usually follows */
export function axisOf(footprints: any, near: any, maxFt = 80): number | null {
  if (!near) return null;
  const o = near, P = toXY(near, o), maxM = maxFt * FT_TO_M;
  let best: any = null;
  (Array.isArray(footprints) ? footprints : []).forEach((f) => { const r = ring(f); for (let i = 0; i < r.length; i++) {
    const A = toXY(r[i], o), B = toXY(r[(i + 1) % r.length], o); const Q = nearestOnSeg(P, A, B); const d = Math.hypot(Q[0] - P[0], Q[1] - P[1]);
    if (d <= maxM && (!best || d < best.d)) best = { d, deg: Math.atan2(B[1] - A[1], B[0] - A[0]) * 180 / Math.PI };
  } });
  return best ? best.deg : null;
}

/* a rectangle from two opposite corners: north-up, or on the house's grid */
export function rectFromCorners(a: any, b: any, axisDeg: number | null = null): [number, number][] {
  if (!a || !b) return [];
  if (axisDeg == null) return [[a[0], a[1]], [a[0], b[1]], [b[0], b[1]], [b[0], a[1]]];
  const o = a; const B = toXY(b, o); const t = axisDeg * Math.PI / 180;
  const u = [Math.cos(t), Math.sin(t)], v = [-Math.sin(t), Math.cos(t)];
  const s = B[0] * u[0] + B[1] * u[1], w = B[0] * v[0] + B[1] * v[1];
  return [[a[0], a[1]], toLL([u[0] * s, u[1] * s], o), toLL([u[0] * s + v[0] * w, u[1] * s + v[1] * w], o), toLL([v[0] * w, v[1] * w], o)];
}

/* the drawn slabs → the estimator's blocks. Per job type the areas add up
   (a lift's sections too) and the kinds become the block's surfaces; a block
   the slabs don't name is left alone, a job type with no block gets one. */
export function applySlabsToBlocks(blocks: any, slabs: any) {
  const list = Array.isArray(blocks) ? blocks.map((b) => ({ ...b })) : [];
  const by: Record<string, { sqft: number; sections: number; surfaces: string[] }> = {};
  (Array.isArray(slabs) ? slabs : []).forEach((s) => {
    if (!s || !s.jobType) return;
    const g = by[s.jobType] || (by[s.jobType] = { sqft: 0, sections: 0, surfaces: [] });
    g.sqft += Number(s.sqft) || 0; g.sections += Number(s.sections) || 0;
    if (s.kind && !g.surfaces.includes(s.kind)) g.surfaces.push(s.kind);
  });
  Object.entries(by).forEach(([jt, g]) => {
    const sqft = Math.round(g.sqft);
    const patch: any = { sqft, surfaces: g.surfaces, fromSlabs: true };
    if (jt === "lift") patch.sections = Math.max(1, g.sections);
    const i = list.findIndex((b) => b && b.jobType === jt);
    if (i >= 0) list[i] = { ...list[i], ...patch }; else list.push({ jobType: jt, ...patch });
  });
  return list;
}

export function slabSummary(slabs: any) {
  const arr = Array.isArray(slabs) ? slabs : [];
  const byJob: Record<string, number> = {};
  let sqft = 0, sections = 0;
  arr.forEach((s) => { const a = Number(s && s.sqft) || 0; sqft += a; byJob[s.jobType] = (byJob[s.jobType] || 0) + a; if (s.jobType === "lift") sections += Number(s.sections) || 0; });
  return { count: arr.length, sqft: Math.round(sqft), sections, byJob };
}

/* a clean copy of what the customer's document may show: kinds, labels,
   rings and square feet — nothing about rates (quote.tsx customerDocOf) */
export function planOf(concrete: any) {
  const c = concrete && typeof concrete === "object" ? concrete : {};
  const slabs = (Array.isArray(c.slabs) ? c.slabs : []).map((s) => ({ kind: s.kind || "other", label: slabLabel(s), poly: ring(s.poly), sqft: Math.round(Number(s.sqft) || 0) })).filter((s) => s.poly.length >= 3);
  if (!slabs.length) return null;
  const site = c.site && typeof c.site === "object" ? c.site : {};
  return { slabs, house: ring(site.house).length >= 3 ? ring(site.house) : null, footprints: (Array.isArray(site.footprints) ? site.footprints : []).map(ring).filter((r) => r.length >= 3) };
}
